import format from "string-format";

import { MapCultureTo, Nullable, NullableResultPromise } from "../base-types";
import { IExtendedContentHubClient } from "../clients/extended-client";
import FlatOptionList from "../contracts/base/option-lists/flat-option-list";
import FlatOptionListValue from "../contracts/base/option-lists/flat-option-list-value";
import HierarchicalOptionList from "../contracts/base/option-lists/hierarchical-option-list";
import HierarchicalOptionListValue from "../contracts/base/option-lists/hierarchical-options-list-value";
import { IOptionList } from "../contracts/base/option-lists/option-list";
import { OptionListBase } from "../contracts/base/option-lists/option-list-base";
import { OptionListType } from "../contracts/base/option-lists/option-list-type";
import {
    IFlatOptionListValue,
    IHierarchicalOptionListValue,
    IOptionListValue,
} from "../contracts/base/option-lists/option-list-value";
import ErrorMessages from "../error-messages";
import Guard from "../guard";
import OptionListResource from "../models/option-lists/option-list-resource";
import OptionListValueResource from "../models/option-lists/option-list-value-resource";

export class OptionListMapper {
    private readonly _client: IExtendedContentHubClient;
    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client, "client");
        this._client = client;
    }

    async mapOptionListAsync(resource: OptionListResource): NullableResultPromise<IOptionList> {
        if (resource === null) {
            return null;
        }

        const createdBy = await this._client.linkHelper.idFromEntityAsync(resource.createdBy!);
        const modifiedBy = await this._client.linkHelper.idFromEntityAsync(resource.modifiedBy!);
        const labels: MapCultureTo<string> = resource.labels || {};

        let olBase: OptionListBase;
        if (resource.type === OptionListType.Flat) {
            const values = this.mapFlatOptionListValues(resource.values);
            olBase = new FlatOptionList(resource.name, labels, values);
        } else if (resource.type === OptionListType.Hierarchical) {
            const values = this.mapHierarchicalOptionListValues(resource.values);
            olBase = new HierarchicalOptionList(resource.name, labels, values);
        } else {
            throw Error(format(ErrorMessages.UnknownOptionListType, resource.type));
        }

        olBase.createdBy = createdBy || undefined;
        olBase.createdOn = resource.createdOn;
        olBase.modifiedBy = modifiedBy || undefined;
        olBase.modifiedOn = resource.modifiedOn;
        olBase.isSystemOwned = resource.isSystemOwned;
        olBase.name = resource.name;

        return olBase;
    }

    map(ol: IOptionList): Nullable<OptionListResource> {
        if (ol === null) {
            return null;
        }

        const type: OptionListType = ol.type;
        const values = this.mapValues(ol.getOptionListValues());
        const resource = new OptionListResource();
        resource.isSystemOwned = ol.isSystemOwned;
        resource.name = ol.name;
        resource.type = type;
        resource.values = values;
        resource.labels = JSON.parse(JSON.stringify(ol.labels));
        return resource;
    }

    mapFlatOptionListValues(optionListValues: Array<OptionListValueResource>): Array<IFlatOptionListValue> {
        if (optionListValues === null) {
            return [];
        }
        const values = optionListValues.map((optionListValue: OptionListValueResource): FlatOptionListValue => {
            return this.mapFlatOptionListValue(optionListValue);
        });
        return values;
    }

    mapFlatOptionListValue(optionListValue: OptionListValueResource): IFlatOptionListValue {
        Guard.notNullOrUndefined(optionListValue);

        return new FlatOptionListValue(optionListValue.identifier, optionListValue.labels);
    }

    mapHierarchicalOptionListValues(
        optionListValues: Array<OptionListValueResource>
    ): Array<IHierarchicalOptionListValue> {
        if (optionListValues === null || optionListValues.length === 0) {
            return [];
        }
        const values = optionListValues.map(
            (optionListValue: OptionListValueResource): IHierarchicalOptionListValue => {
                return this.mapHierarchicalOptionListValue(optionListValue);
            }
        );
        return values;
    }

    mapHierarchicalOptionListValue(optionListValue: OptionListValueResource): IHierarchicalOptionListValue {
        Guard.notNullOrUndefined(optionListValue);

        let values: Array<IHierarchicalOptionListValue>;

        if (optionListValue.values && optionListValue.values.length) {
            values = this.mapHierarchicalOptionListValues(optionListValue.values);
        } else {
            values = [];
        }

        return new HierarchicalOptionListValue(optionListValue.identifier, optionListValue.labels, values);
    }

    mapValues(optionListValues: Array<IOptionListValue>): Array<OptionListValueResource> {
        if (optionListValues === null || optionListValues.length === 0) {
            return [];
        }

        const list: Array<OptionListValueResource> = [];
        optionListValues.forEach((optionListValue: IOptionListValue) => {
            const olv = this.mapValue(optionListValue);
            list.push(olv);
        });
        return list;
    }

    mapValue(optionListValue: IOptionListValue): OptionListValueResource {
        Guard.notNullOrUndefined(optionListValue);

        const value = new OptionListValueResource();
        value.identifier = optionListValue.identifier;
        value.labels = optionListValue.labels;

        if (optionListValue instanceof HierarchicalOptionListValue) {
            const subValues = this.mapValues(optionListValue.values);
            value.values = subValues;
        }
        return value;
    }
}
